
import { useState, useMemo } from 'react';
import { allParksAndFacilities } from '../../../mocks/parks';

const typeColors: Record<
  string,
  { bg: string; text: string; dot: string }
> = {
  Trails: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
  },
  Playgrounds: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    dot: 'bg-amber-500',
  },
  'Sports Facilities': {
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    dot: 'bg-rose-500',
  },
  'Dog Parks': {
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    dot: 'bg-sky-500',
  },
  'Nature Preserves': {
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    dot: 'bg-violet-500',
  },
};

const typeIcons: Record<string, string> = {
  Trails: 'ri-route-line',
  Playgrounds: 'ri-gamepad-line',
  'Sports Facilities': 'ri-trophy-line',
  'Dog Parks': 'ri-bear-smile-line',
  'Nature Preserves': 'ri-leaf-line',
};

interface ParksMapViewProps {
  activeCategory: string;
}

export default function ParksMapView({
  activeCategory,
}: ParksMapViewProps) {
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = useMemo(() => {
    let items = allParksAndFacilities;
    if (activeCategory !== 'All') {
      items = items.filter((item) => item.type === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (item) =>
          item.name.toLowerCase().includes(q) ||
          item.address.toLowerCase().includes(q) ||
          item.type.toLowerCase().includes(q)
      );
    }
    return items;
  }, [activeCategory, searchQuery]);

  const selectedPark = useMemo(
    () => filtered.find((p) => p.id === selectedItem) || null,
    [filtered, selectedItem]
  );

  // Build Google Maps embed URL with markers
  const mapSrc = useMemo(() => {
    if (selectedPark) {
      return `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(
        selectedPark.address
      )}&zoom=15&maptype=roadmap`;
    }
    // Default: show Roseville CA centered
    return `https://www.google.com/maps/embed/v1/search?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=parks+and+trails+in+Roseville+CA&zoom=12&maptype=roadmap`;
  }, [selectedPark]);

  return (
    <section id="map-view" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full text-xs font-semibold mb-3">
              <i className="ri-map-pin-line"></i>
              INTERACTIVE MAP
            </div>
            <h2 className="text-3xl font-bold text-gray-900">
              Explore All Locations
            </h2>
            <p className="text-gray-500 mt-1 text-sm">
              Click any location to see it on the map &middot;{' '}
              {filtered.length} places shown
            </p>
          </div>
          <div className="relative w-full md:w-72">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder="Search parks, trails..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mb-6">
          {Object.entries(typeColors).map(([type, colors]) => (
            <div
              key={type}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}
            >
              <span
                className={`w-2 h-2 rounded-full ${colors.dot}`}
              ></span>
              <i className={`${typeIcons[type]} text-xs`}></i>
              {type}
            </div>
          ))}
        </div>

        {/* Map + Sidebar Layout */}
        <div className="grid lg:grid-cols-[380px_1fr] gap-6 h-[640px]">
          {/* Sidebar List */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {filtered.length} Location
                {filtered.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 p-6">
                  <i className="ri-map-pin-line text-3xl mb-2"></i>
                  <p className="text-sm">No locations found</p>
                </div>
              ) : (
                filtered.map((item) => {
                  const colors =
                    typeColors[item.type] || typeColors.Trails;
                  const isSelected = selectedItem === item.id;
                  const isHovered = hoveredItem === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() =>
                        setSelectedItem(isSelected ? null : item.id)
                      }
                      onMouseEnter={() => setHoveredItem(item.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                      className={`w-full text-left px-4 py-3.5 border-b border-gray-50 transition-all cursor-pointer ${
                        isSelected
                          ? 'bg-emerald-50 border-l-4 border-l-emerald-500'
                          : isHovered
                          ? 'bg-gray-50'
                          : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4
                              className={`text-sm font-semibold truncate ${
                                isSelected
                                  ? 'text-emerald-700'
                                  : 'text-gray-900'
                              }`}
                            >
                              {item.name}
                            </h4>
                            <div className="flex items-center gap-0.5 flex-shrink-0">
                              <i className="ri-star-fill text-amber-400 text-xs"></i>
                              <span className="text-xs font-medium text-gray-600">
                                {item.rating}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${colors.bg} ${colors.text}`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${colors.dot}`}
                              ></span>
                              {item.type}
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 mt-1 truncate">
                            <i className="ri-map-pin-2-line mr-1"></i>
                            {item.address}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Map Area */}
          <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-white">
            <iframe
              title="Roseville Parks Map"
              src={mapSrc}
              className="w-full h-full border-0"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>

            {/* Selected Park Info Overlay */}
            {selectedPark && (
              <div className="absolute bottom-4 left-4 right-4 md:left-4 md:right-auto md:w-96 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden animate-slideUp">
                <div className="flex gap-0">
                  <div className="w-28 flex-shrink-0">
                    <img
                      src={selectedPark.image}
                      alt={selectedPark.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 p-3.5">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="text-sm font-bold text-gray-900 leading-tight">
                        {selectedPark.name}
                      </h4>
                      <button
                        onClick={() => setSelectedItem(null)}
                        className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex-shrink-0 cursor-pointer"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          (typeColors[selectedPark.type] ||
                            typeColors.Trails).bg
                        } ${
                          (typeColors[selectedPark.type] ||
                            typeColors.Trails).text
                        }`}
                      >
                        {selectedPark.type}
                      </span>
                      <div className="flex items-center gap-0.5">
                        <i className="ri-star-fill text-amber-400 text-[10px]"></i>
                        <span className="text-[11px] font-medium text-gray-600">
                          {selectedPark.rating} ({selectedPark.reviews})
                        </span>
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1.5 flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {selectedPark.hours}
                    </p>
                    <div className="flex gap-2 mt-2.5">
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                          selectedPark.address
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-direction-line"></i>
                        Directions
                      </a>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                          selectedPark.address
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold px-3 py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-external-link-line"></i>
                        View
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
